import { attach, createEvent, sample } from 'effector'

import { $clientRuntime, $gameConnectConfig, gameConnectFinished } from '@/entities/game'
import * as portalModel from '@/entities/profile/portal'
import { crashlogAPI } from '@/shared/api'
import type { ReportSubmitRequest } from '@/shared/api/crashlog/types'

import {
    crashlogTags,
    enrichCrashlogV2WithBrowserState,
    enrichCrashlogWithBrowserState,
    formatClientCrashlogV2,
    formatConnectCrashlog,
    formatCrashlogSubmit,
} from './lib'
import type { SendCrashlogParams, SendCrashlogV2Params } from './types'

export const clientCrashed = createEvent<SendCrashlogParams>()
export const clientCrashedV2 = createEvent<SendCrashlogV2Params>()

const crashlogSubmitFx = attach({
    source: { profile: portalModel.$portalProfile, connectConfig: $gameConnectConfig, clientRuntime: $clientRuntime },
    mapParams: (params: SendCrashlogParams, { profile, connectConfig, clientRuntime }): ReportSubmitRequest => ({
        client_version: connectConfig?.client_version,
        deploy_version: connectConfig?.info.deploy_version,
        public_id: profile?.publicId,
        support_id: profile?.supportId,
        ...formatCrashlogSubmit(params),
        tags: crashlogTags(params.tags, clientRuntime),
    }),
    effect: crashlogAPI.crashlogSubmit,
})

/**
 * The client log a support ticket carries. No crash happened: the crashlog service is only the
 * place the log is stored, so this never reaches `crashReported`.
 */
export const ticketLogSubmit = createEvent<SendCrashlogParams>()

sample({
    clock: ticketLogSubmit,
    fn: enrichCrashlogWithBrowserState,
    target: crashlogSubmitFx,
})

// A client crash takes its own handle on `crashlogSubmitFx`, so `crashReported` and
// `crashReportFailed` below can tell a crash apart from the connect failures and support-ticket
// logs that reach the crashlog service through the very same effect.
const crashReportFx = attach({
    effect: crashlogSubmitFx,
    mapParams: enrichCrashlogWithBrowserState,
})

sample({
    clock: clientCrashed,
    target: crashReportFx,
})

const crashlogV2SubmitFx = attach({
    effect: crashlogAPI.crashlogSubmit,
    source: { profile: portalModel.$portalProfile, connectConfig: $gameConnectConfig, clientRuntime: $clientRuntime },
    mapParams: (params: SendCrashlogV2Params, { profile, connectConfig, clientRuntime }): ReportSubmitRequest => ({
        client_version: connectConfig?.client_version,
        deploy_version: connectConfig?.info.deploy_version,
        public_id: profile?.publicId,
        support_id: profile?.supportId,
        ...formatClientCrashlogV2(params),
        tags: crashlogTags(params.tags, clientRuntime),
    }),
})

sample({
    clock: clientCrashedV2,
    fn: enrichCrashlogV2WithBrowserState,
    target: crashlogV2SubmitFx,
})

/**
 * A client crash the crashlog service stored, carrying the `error_message` it was stored under. A
 * connect failure and a support-ticket log are reports too, but a support ticket names neither, so
 * only the two crash paths report here.
 */
export const crashReported = createEvent<{ reportId: string; message: string }>()

/** The crash never reached the crashlog service, so there is no report left to point at. */
export const crashReportFailed = createEvent()

sample({
    clock: crashReportFx.done,
    fn: ({ params, result }) => ({ reportId: result.report_id, message: params.message }),
    target: crashReported,
})

sample({
    clock: crashlogV2SubmitFx.done,
    // Rebuild the message the service was given: the formatter is what produced `error_message`.
    fn: ({ params, result }) => ({ reportId: result.report_id, message: formatClientCrashlogV2(params).error_message }),
    target: crashReported,
})

sample({
    clock: [crashReportFx.fail, crashlogV2SubmitFx.fail],
    target: crashReportFailed,
})

const connectFailed = gameConnectFinished.filterMap(({ attempt, result }) =>
    result.type === 'error' ? { phase: attempt.phase, error: result.error } : undefined
)

sample({
    clock: connectFailed,
    fn: (failure) => enrichCrashlogWithBrowserState(formatConnectCrashlog(failure)),
    target: crashlogSubmitFx,
})
